import { useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  useAlerts,
  useDeleteAlert,
  useDeliveries,
  useIsAdmin,
  type AlertRule,
  useRunSchedule,
  useScheduleRuns,
  useSchedulerStatus,
  useSchedules,
  useTestSchedule,
  type Delivery,
  type Schedule,
} from "@/api/queries";
import { Button } from "@/components/ui/Button";
import { Chip, StatusDot, type Tone } from "@/components/ui/Chip";
import { EmptyState } from "@/components/ui/EmptyState";
import { Spinner } from "@/components/ui/Spinner";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { AlertState } from "@/components/reports/AlertsDialog";
import { describeRule } from "@/components/reports/alertText";
import { useFormatter, type ValueFormat } from "@/artifacts/format";
import {
  AlertIcon,
  BellIcon,
  CalendarIcon,
  ChevronRightIcon,
  ClockIcon,
  MailIcon,
  RefreshIcon,
  TrashIcon,
} from "@/components/icons";
import { cn } from "@/lib/cn";
import { useRelativeTime } from "@/lib/time";

/**
 * Schedules and delivery (PRD FR-8, FR-12.1).
 *
 * Two halves, and the second is the one that matters at 07:00: the delivery
 * log. A schedule that fired and a schedule that delivered are different
 * facts, and an operator whose report did not arrive needs to know which of
 * them failed — so a firing's history and the emails it produced are on the
 * same page, and a failure carries the SMTP server's own words.
 *
 * `skipped` is shown as loudly as `failed`. A deployment with no SMTP host
 * refreshes and snapshots and sends nothing, which is a legitimate
 * configuration and a terrible surprise; the log says so rather than staying
 * empty and looking healthy.
 */

function statusTone(status: string): Tone {
  if (status === "sent" || status === "ok") return "success";
  if (status === "failed") return "error";
  return "warning";
}

function DeliveryRow({ delivery }: { delivery: Delivery }) {
  const stamp = useRelativeTime(delivery.occurred_at);
  return (
    <li className="border-border flex flex-col gap-1 border-b py-2.5 last:border-b-0">
      <div className="flex flex-wrap items-center gap-2">
        <StatusDot tone={statusTone(delivery.status)} />
        <span className="type-label text-ink min-w-0 flex-1 truncate">{delivery.subject}</span>
        {delivery.trigger === "test" ? <Chip tone="neutral">test</Chip> : null}
        <span className="type-caption text-ink-disabled normal-case">{stamp}</span>
      </div>
      <p className="type-caption text-ink-secondary normal-case">
        {delivery.recipients.length
          ? delivery.recipients.join(", ")
          : "no recipients"}
        {delivery.attachment_bytes
          ? ` · ${Math.round(delivery.attachment_bytes / 1024)} kB attached`
          : ""}
        {delivery.duration_ms ? ` · ${delivery.duration_ms} ms` : ""}
      </p>
      {delivery.error_message ? (
        <p className="type-caption text-warning flex items-start gap-1.5 normal-case">
          <AlertIcon className="mt-0.5 size-3 shrink-0" />
          <span>
            {delivery.error_code ? `${delivery.error_code}: ` : ""}
            {delivery.error_message}
          </span>
        </p>
      ) : null}
    </li>
  );
}

function ScheduleRow({ schedule }: { schedule: Schedule }) {
  const [open, setOpen] = useState(false);
  const run = useRunSchedule();
  const test = useTestSchedule();
  const isAdmin = useIsAdmin();
  const { data: runs } = useScheduleRuns(schedule.id, open);
  const last = useRelativeTime(schedule.last_fired_at);

  return (
    <li className="border-border rounded-container border">
      <div className="flex flex-wrap items-center gap-3 px-4 py-3">
        <StatusDot tone={schedule.enabled ? statusTone(schedule.last_status ?? "ok") : "neutral"} />
        <div className="min-w-0 flex-1">
          <p className="type-label text-ink flex items-center gap-2 truncate">
            <span className="truncate">{schedule.name}</span>
            {/* Derived from the run history (ALERT-1): the surface that still
                works when SMTP is the thing that broke. */}
            {schedule.failing ? (
              <Chip tone="error">
                failing · {schedule.failing_runs} run{schedule.failing_runs === 1 ? "" : "s"}
              </Chip>
            ) : null}
          </p>
          <p className="type-caption text-ink-secondary normal-case">
            {/* A preset describes itself without naming the zone, so the row
                adds it; a raw cron expression already carries its own. */}
            {schedule.preset ? `${schedule.description} · ${schedule.timezone}` : schedule.description}
            {schedule.recipients.length
              ? ` · ${schedule.recipients.join(", ")}`
              : " · no recipients"}
          </p>
          {schedule.failing && schedule.failing_reason ? (
            <p className="type-support text-error mt-0.5">
              {schedule.failing_reason}
              {schedule.failing_since
                ? ` — since ${new Date(schedule.failing_since).toLocaleString()}`
                : ""}
            </p>
          ) : null}
        </div>
        <div className="type-caption text-ink-disabled text-end normal-case">
          {schedule.enabled ? (
            <p>
              next{" "}
              {schedule.next_fire_at
                ? new Date(schedule.next_fire_at).toLocaleString()
                : "never — check the expression"}
            </p>
          ) : (
            <p>disabled</p>
          )}
          {schedule.last_fired_at ? <p>last fired {last}</p> : null}
        </div>
        <Link
          to={schedule.owner_kind === "report" ? "/reports/$reportId" : "/dashboards/$dashboardId"}
          params={
            schedule.owner_kind === "report"
              ? { reportId: schedule.owner_id }
              : { dashboardId: schedule.owner_id }
          }
          className="type-caption text-accent normal-case hover:underline"
        >
          open {schedule.owner_kind}
        </Link>
        <Button
          size="sm"
          icon={<RefreshIcon className="size-3.5" />}
          loading={run.isPending}
          onClick={() => run.mutate(schedule.id)}
        >
          Run now
        </Button>
        {/* Sends real email to the recipients: the Admin tier's (ACC-2). */}
        {isAdmin ? (
          <Button
            size="sm"
            icon={<MailIcon className="size-3.5" />}
            loading={test.isPending}
            onClick={() => test.mutate(schedule.id)}
          >
            Test
          </Button>
        ) : null}
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
          className="text-ink-secondary hover:text-ink hover:bg-hover rounded-element motion-fast inline-flex size-7 items-center justify-center"
        >
          <ChevronRightIcon className={cn("size-4 transition-transform", open && "rotate-90")} />
        </button>
      </div>

      {schedule.last_detail ? (
        <p className="type-caption text-ink-secondary border-border border-t px-4 py-2 normal-case">
          {schedule.last_detail}
        </p>
      ) : null}

      {open ? (
        <div className="border-border border-t px-4 py-3">
          <p className="type-caption text-ink-disabled mb-2">
            Firings — one row per window. A restart cannot add a second row for a window
            already delivered.
          </p>
          <ul className="flex flex-col gap-1.5">
            {(runs ?? []).map((entry) => (
              <li key={entry.id} className="type-caption text-ink-secondary flex flex-wrap gap-2 normal-case">
                <StatusDot tone={statusTone(entry.status)} />
                <span className="text-ink font-mono">{entry.fire_key}</span>
                <span>
                  {entry.queries} quer{entry.queries === 1 ? "y" : "ies"} · {entry.tiles} tiles
                  {entry.failures ? ` · ${entry.failures} failed` : ""}
                </span>
                {entry.catchup ? <Chip tone="warning">catch-up</Chip> : null}
                {entry.error_message ? (
                  <span className="text-warning">{entry.error_message}</span>
                ) : null}
              </li>
            ))}
            {!runs?.length ? (
              <li className="type-caption text-ink-disabled normal-case">
                This schedule has not fired yet.
              </li>
            ) : null}
          </ul>
        </div>
      ) : null}
    </li>
  );
}

/**
 * Every threshold rule in the deployment, with its state (ALERT-2). Rules are
 * made from a report's trend or header; this is where they are all in one
 * place, for the person asking "what are we watching?".
 */
function AlertRules() {
  const { data: rules, isLoading } = useAlerts();
  const isAdmin = useIsAdmin();
  const formatter = useFormatter();
  const remove = useDeleteAlert();
  const [deleting, setDeleting] = useState<AlertRule | null>(null);
  return (
    <section className="mb-8">
      <h2 className="type-h3 text-ink mb-3">Alerts</h2>
      {isLoading ? (
        <Spinner />
      ) : rules && rules.length ? (
        <ul className="flex flex-col gap-2">
          {rules.map((rule) => (
            <li
              key={rule.id}
              className="border-border rounded-container flex flex-wrap items-center gap-3 border px-4 py-3"
            >
              <BellIcon className="text-ink-disabled size-4" />
              <div className="min-w-48 flex-1">
                <p className="type-label text-ink">
                  {rule.name || rule.metric_label}{" "}
                  <span className="text-ink-secondary font-normal">
                    {describeRule(rule, (value, kind) => formatter.value(value, kind))}
                  </span>
                </p>
                <p className="type-caption text-ink-disabled normal-case">
                  {rule.latest?.value !== null && rule.latest?.value !== undefined
                    ? `last ${formatter.value(rule.latest.value, (rule.metric_format || "number") as ValueFormat)} · `
                    : ""}
                  {rule.recipients?.length ? rule.recipients.join(", ") : "operator address"}
                  {rule.latest?.note ? ` · ${rule.latest.note}` : ""}
                </p>
              </div>
              <AlertState rule={rule} />
              <Link
                to={rule.subject_kind === "report" ? "/reports/$reportId" : "/dashboards/$dashboardId"}
                params={
                  rule.subject_kind === "report"
                    ? { reportId: rule.subject_id }
                    : { dashboardId: rule.subject_id }
                }
                className="type-caption text-accent normal-case hover:underline"
              >
                open {rule.subject_kind}
              </Link>
              {isAdmin ? (
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<TrashIcon className="size-3.5" />}
                  onClick={() => setDeleting(rule)}
                >
                  Delete
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="type-support text-ink-secondary">
          No alerts yet. Open a report&rsquo;s Trend and choose &ldquo;Alert me&rdquo; on a figure to
          be told when it moves.
        </p>
      )}
      <ConfirmDialog
        open={Boolean(deleting)}
        onClose={() => setDeleting(null)}
        loading={remove.isPending}
        title="Delete this alert?"
        description={<>“{deleting?.name || deleting?.metric_label}” will stop being evaluated.</>}
        consequence={<>Its history goes too — every evaluation it recorded.</>}
        onConfirm={() => {
          if (!deleting) return;
          remove.mutate(deleting.id, { onSuccess: () => setDeleting(null) });
        }}
      />
    </section>
  );
}

export function SchedulesPage() {
  const { data: schedules, isLoading } = useSchedules();
  const { data: status } = useSchedulerStatus();
  const [filter, setFilter] = useState<string>("");
  const isAdmin = useIsAdmin();
  const { data: deliveries } = useDeliveries(filter ? { status: filter } : {}, isAdmin);

  return (
    <div className="mx-auto flex h-full min-h-0 w-full max-w-6xl flex-col">
      <header className="border-border flex flex-col gap-3 border-b px-6 py-4">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="type-h2 text-ink flex-1">Schedules &amp; alerts</h1>
          {status ? (
            <>
              <Chip tone={status.running ? "success" : status.enabled ? "error" : "neutral"}>
                {status.running
                  ? "scheduler running"
                  : status.enabled
                    ? "scheduler not running"
                    : "scheduler disabled"}
              </Chip>
              <Chip tone={status.email_configured ? "neutral" : "warning"}>
                {status.email_configured ? "SMTP configured" : "no SMTP server"}
              </Chip>
              <Chip tone={status.pdf_renderer === "none" ? "warning" : "neutral"}>
                {status.pdf_renderer === "none" ? "no PDF renderer" : "PDF: Chromium"}
              </Chip>
              {status.failing_schedules ? (
                <Chip tone="error">
                  {status.failing_schedules} failing
                </Chip>
              ) : null}
            </>
          ) : null}
        </div>
        {status ? (
          <p className="type-support text-ink-secondary">
            Cron runs in {status.timezone}. On boot, windows missed in the last{" "}
            {status.catchup_hours} hours are delivered once — the claim on each window is a
            database constraint, so a restart mid-window cannot send it twice.
          </p>
        ) : null}
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
        <section className="mb-8">
          <h2 className="type-h3 text-ink mb-3">Schedules</h2>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          ) : schedules?.length ? (
            <ul className="flex flex-col gap-3">
              {schedules.map((schedule) => (
                <ScheduleRow key={schedule.id} schedule={schedule} />
              ))}
            </ul>
          ) : (
            <EmptyState
              icon={<CalendarIcon className="size-6" />}
              title="Nothing is scheduled"
              detail="Open a report or a dashboard and press Schedule. It will refresh, take a snapshot, and email whoever you name."
            />
          )}
        </section>

        <AlertRules />

        {/* Who was emailed what is the Admin tier's (ACC-2). */}
        {isAdmin ? (
        <section>
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <h2 className="type-h3 text-ink flex-1">Delivery log</h2>
            {(["", "sent", "failed", "skipped"] as const).map((value) => (
              <button
                key={value || "all"}
                type="button"
                onClick={() => setFilter(value)}
                className={cn(
                  "type-caption rounded-element motion-fast border px-2 py-1 normal-case",
                  filter === value
                    ? "border-accent bg-accent-subtle text-accent"
                    : "border-border text-ink-secondary hover:bg-hover",
                )}
              >
                {value || "all"}
              </button>
            ))}
          </div>
          {deliveries?.length ? (
            <ul className="border-border rounded-container border px-4">
              {deliveries.map((delivery) => (
                <DeliveryRow key={delivery.id} delivery={delivery} />
              ))}
            </ul>
          ) : (
            <EmptyState
              icon={<ClockIcon className="size-6" />}
              title="Nothing delivered yet"
              detail="Every attempt appears here — sent, failed or skipped — with the server's own message when one went wrong."
            />
          )}
        </section>
        ) : null}
      </div>
    </div>
  );
}
